import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { SearchType, TagOption, Field } from '@tw/tw-component-library';
import { mapUsersToSuggestions, toTagOptions } from '../utils/transformers';
import { isStaleTagPickerReAdd } from '../utils/tagPickerFields';
import { useFormContext } from '../contexts/useFormContext';
import {
  usePartnerSearch,
  useFeeEarnerSearch,
  useDefaultSearch,
} from '../components/container/forms/hooks';
import { User } from '../components/container/forms/types/api/graph.types';
import { createDebounce } from '../utils/debounce';

// Type for debounced function
type DebouncedFetchFunction = {
  (searchTerm: string, fieldName: string, searchType: SearchType): void;
  cancel: () => void;
};

/** Mirrored second partner fields — kept in sync on change/clear (see applySecondPartnerMirror). */
const SECOND_PARTNER_FIELDS = [
  'second_partner',
  'second_partner_above_25k',
  'second_partner_below_25k',
] as const;

const isSecondPartnerField = (fieldName: string): boolean =>
  (SECOND_PARTNER_FIELDS as readonly string[]).includes(fieldName);

const EMPTY_PRE_TAGS: TagOption[] = [];

const getTagIdsFromFieldValue = (value: unknown): string[] => {
  return toTagOptions(value).map((tag) => tag.value);
};

const buildSecondPartnerFormUpdate = (
  fieldName: string,
  newTags: TagOption[]
): Record<string, TagOption[]> => {
  if (newTags.length === 0) {
    return SECOND_PARTNER_FIELDS.reduce(
      (acc, field) => {
        acc[field] = [];
        return acc;
      },
      {} as Record<string, TagOption[]>
    );
  }

  const updates: Record<string, TagOption[]> = {
    [fieldName]: newTags,
    second_partner: newTags,
  };

  SECOND_PARTNER_FIELDS.forEach((field) => {
    if (field !== fieldName && field !== 'second_partner') {
      updates[field] = [];
    }
  });

  return updates;
};

export const useTagPickerOperations = () => {
  const { formState, updateFormData } = useFormContext();

  // Use the new unified search hooks
  const partnerSearch = usePartnerSearch({
    setState: () => {}, // We'll handle state locally
    setTagPickerSuggestions: () => {}, // We'll handle suggestions locally
    minSearchLength: 3,
  });

  const feeEarnerSearch = useFeeEarnerSearch({
    setState: () => {}, // We'll handle state locally
    setTagPickerSuggestions: () => {}, // We'll handle suggestions locally
    minSearchLength: 3,
  });

  const usersSearch = useDefaultSearch({
    setState: () => {}, // We'll handle state locally
    setTagPickerSuggestions: () => {}, // We'll handle suggestions locally
    minSearchLength: 3,
  });

  // State with separate variables to avoid unnecessary re-renders
  const [suggestions, setSuggestions] = useState<Record<string, TagOption[]>>(
    {}
  );
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>(
    {}
  );
  const [pendingRemovals, setPendingRemovals] = useState<
    Record<string, string[]>
  >({});

  // Keep track of the last search term to prevent duplicate API calls
  const lastSearchTermRef = useRef<Record<string, string>>({});

  // Create a stable debounced search function reference
  const debouncedFetchRef = useRef<DebouncedFetchFunction | null>(null);

  // Field name to search type mapping
  const fieldNameToSearchType: Record<string, SearchType> = useMemo(
    () => ({
      // Dubai Payment Approval tagPicker
      responsible_lawyer: 'feeEarner',
      first_partner: 'partner',
      second_partner: 'partner',
      second_partner_above_25k: 'partner',
      second_partner_below_25k: 'partner',
      // Dubai Payment From Office Account tagPicker fields
      approver: 'default',
      // Dubai Transfer Requisition tagPicker fields
      firstPartner: 'partner',
      secondPartner: 'partner',
      feeEarner: 'feeEarner',
      // Overtime Approval tagPicker fields
      line_manager: 'default',
      givenReceivedByPerson: 'default',
      authoriser: 'default',
      // Default tagPicker fields
      default: 'default',
    }),
    []
  );

  // Function to determine which search function to use
  const getSearchFunction = useCallback(
    (
      searchType: SearchType
    ): ((searchTerm: string) => Promise<User[] | TagOption[]>) => {
      // Handle search types
      switch (searchType) {
        case 'partner':
          return partnerSearch.searchUsers;
        case 'feeEarner':
          return feeEarnerSearch.searchUsers;
        case 'default':
        default:
          return usersSearch.searchUsers;
      }
    },
    [
      partnerSearch.searchUsers,
      feeEarnerSearch.searchUsers,
      usersSearch.searchUsers,
    ]
  );

  // Initialize the debounced function only once
  useEffect(() => {
    debouncedFetchRef.current = createDebounce(
      async (searchTerm: string, fieldName: string, searchType: SearchType) => {
        // Skip if this exact search was already performed
        if (lastSearchTermRef.current[fieldName] === searchTerm) {
          return;
        }

        lastSearchTermRef.current[fieldName] = searchTerm;

        try {
          // Get the appropriate search function based on search type
          const searchFunction = getSearchFunction(searchType);

          // Perform the search
          const results = await searchFunction(searchTerm);

          // Handle different return types
          let newSuggestions: TagOption[] = [];
          if (Array.isArray(results)) {
            if (
              results.length > 0 &&
              'value' in results[0] &&
              'label' in results[0]
            ) {
              // Already TagOption[] format (client/matter search)
              newSuggestions = results as TagOption[];
            } else {
              newSuggestions = mapUsersToSuggestions(
                results as User[],
                fieldName === 'line_manager' ||
                  fieldName === 'givenReceivedByPerson' ||
                  fieldName === 'authoriser'
                  ? { valueField: 'mail' }
                  : undefined
              );
            }
          }

          setSuggestions((prev) => ({
            ...prev,
            [fieldName]: newSuggestions,
          }));
          setLoadingFields((prev) => ({ ...prev, [fieldName]: false }));
        } catch (error) {
          console.error(`Error fetching data: ${error}`);
          setSuggestions((prev) => ({
            ...prev,
            [fieldName]: [],
          }));
          setLoadingFields((prev) => ({ ...prev, [fieldName]: false }));
        }
      },
      300
    );

    return () => {
      if (debouncedFetchRef.current?.cancel) {
        debouncedFetchRef.current.cancel();
      }
    };
  }, [getSearchFunction]);

  // Function to determine search type for a field
  const getSearchTypeForField = useCallback(
    (field: Field): SearchType => {
      // Check for explicit search type in field extras
      if (field.extras?.tagPicker?.searchType) {
        return field.extras.tagPicker.searchType as SearchType;
      }

      // Otherwise use the mapping by field name
      return fieldNameToSearchType[field.name] || fieldNameToSearchType.default;
    },
    [fieldNameToSearchType]
  );

  const handleTagPickerSearch = useCallback(
    async (
      fieldName: string,
      searchTerm: string,
      searchType: SearchType
    ): Promise<TagOption[]> => {
      if (!searchTerm || searchTerm.length < 3) {
        setSuggestions((prev) => ({
          ...prev,
          [fieldName]: [],
        }));
        return [];
      }

      // Only set loading if it wasn't already loading
      if (!loadingFields[fieldName]) {
        setLoadingFields((prev) => ({ ...prev, [fieldName]: true }));
      }

      // Use the debounced function to prevent rapid repeated searches
      if (debouncedFetchRef.current) {
        debouncedFetchRef.current(searchTerm, fieldName, searchType);
      }

      // Return the current suggestions while the debounced search is in progress
      return suggestions[fieldName] || [];
    },
    [loadingFields, suggestions]
  );

  const handleTagsChange = useCallback(
    (fieldName: string, newTags: TagOption[]) => {
      const currentValueIds = new Set(
        getTagIdsFromFieldValue(formState.data[fieldName])
      );
      const normalizedTags = toTagOptions(newTags);
      const newValueIds = new Set(normalizedTags.map((tag) => tag.value));
      const pendingIds = pendingRemovals[fieldName] || [];
      const isStaleReAdd = isStaleTagPickerReAdd(
        [...currentValueIds],
        [...newValueIds],
        pendingIds
      );

      if (isStaleReAdd) {
        return;
      }

      const hasChanges =
        currentValueIds.size !== newValueIds.size ||
        [...newValueIds].some((id) => !currentValueIds.has(id));

      if (!hasChanges) {
        return;
      }

      const removedTags = [...currentValueIds].filter(
        (id) => !newValueIds.has(id)
      );

      if (removedTags.length > 0) {
        setPendingRemovals((prev) => ({
          ...prev,
          [fieldName]: removedTags,
        }));
      } else if (normalizedTags.length > 0) {
        setPendingRemovals((prev) => ({
          ...prev,
          [fieldName]: [],
        }));
      }

      if (isSecondPartnerField(fieldName)) {
        updateFormData(buildSecondPartnerFormUpdate(fieldName, normalizedTags));
        return;
      }

      updateFormData({
        [fieldName]: normalizedTags,
      });
    },
    [formState.data, pendingRemovals, updateFormData]
  );

  const handleRemovalConfirmation = useCallback((fieldName: string) => {
    setPendingRemovals((prev) => ({
      ...prev,
      [fieldName]: [],
    }));
  }, []);

  // Memoize the tag picker props creation for each field
  const getTagPickerProps = useCallback(
    (field: Field, fieldName: string) => {
      const searchType = getSearchTypeForField(field);

      return {
        onSearch: (searchTerm: string) =>
          handleTagPickerSearch(fieldName, searchTerm, searchType),
        onTagsChange: (newTags: TagOption[]) =>
          handleTagsChange(fieldName, newTags),
        suggestions: suggestions[fieldName] || [],
        // The new FormTagPicker syncs local chips from preTags whenever the
        // tag values change. Live form tags here would restore a person after
        // X. Initial chips come from FormStack formValue / initialData.
        preTags: EMPTY_PRE_TAGS,
        isLoading: loadingFields[fieldName] || false,
        minSearchLength: field.extras?.tagPicker?.minSearchLength || 3,
        maxTags: field.extras?.tagPicker?.maxTags || 1,
        createNewTags: field.extras?.tagPicker?.createNewTags ?? false,
        pendingRemovals: pendingRemovals[fieldName] || [],
        onRemovalConfirm: () => handleRemovalConfirmation(fieldName),
      };
    },
    [
      suggestions,
      loadingFields,
      pendingRemovals,
      handleTagPickerSearch,
      handleTagsChange,
      handleRemovalConfirmation,
      getSearchTypeForField,
    ]
  );

  const processTagPickerField = useCallback(
    (field: Field) => {
      if (field.type === 'tagPicker') {
        const tagPickerProps = getTagPickerProps(field, field.name);

        return {
          ...field,
          extras: {
            ...field.extras,
            tagPicker: tagPickerProps,
          },
        };
      }
      return field;
    },
    [getTagPickerProps]
  );

  return {
    processTagPickerField,
    tagPickerState: {
      suggestions,
      loadingFields,
      pendingRemovals,
    },
  };
};
